import React, { useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { backbutton, poweredBy } from "./svgs";
import Spinner from "../Spinner";
import { CheckoutForm } from "./CheckoutForm";
import { MarketBasketItem } from "./MarketModal";
import { CryptoCheckoutForm } from "./CryptoCheckoutForm";

interface MarketPaymentPageProps {
  domain: string;
  checkoutId: string;
  basket?: MarketBasketItem;
  stripe: {
    stripe: Stripe | Promise<Stripe | null>;
    connectedAccountId: string;
  };
  currencyEstimates: [
    {
      symbol: string;
      name: string;
      exhangeRate: number;
    }
  ];
  userInfo: any;
  apiUrl: any;
  demo: boolean;
  skipPayment: boolean;
  setCheckoutId: (checkoutId: string) => void;
  setHeight: (height: any) => void;
  back: () => void;
  next: () => void;
}

export const MarketPaymentPage: React.FC<MarketPaymentPageProps> = ({
  domain,
  stripe,
  userInfo,
  apiUrl,
  checkoutId,
  basket,
  currencyEstimates,
  demo,
  skipPayment,
  setCheckoutId,
  setHeight,
  back,
  next,
}) => {
  const [clientSecret, setClientSecret] = useState("");

  let widthStyle = "100%"; // default
  if (navigator.userAgent.includes("Firefox")) {
    widthStyle = "-moz-available";
  } else if (
    navigator.userAgent.includes("Chrome") ||
    navigator.userAgent.includes("Safari")
  ) {
    widthStyle = "-webkit-fill-available";
  } else {
    widthStyle = "fill-available";
  }

  useEffect(() => {
    setHeight(650)
  },[])

  const stripePurchase = () => {
    fetch(`${apiUrl}/v1/market/purchase`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        domain: domain,
        ownerAddress: userInfo.walletAddress,
        stripeConnectedAccountId: stripe.connectedAccountId,
        checkoutId: checkoutId,
        externalUserId: userInfo.externalUserId,
        demo: demo,
        years: basket?.years,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setClientSecret(data.stripePaymentIntent)
      });
  }

  const completePaymentIntent = (paymentIntentId: string) => {
    return fetch(`${apiUrl}/v1/market/test/payment-intent/${paymentIntentId}/complete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: null,
    })
  }

  const cryptoPurchase = async (currency: string, ownerEmail: string, ownerName: string): Promise<void> => {
   return fetch(`${apiUrl}/v1/market/crypto/purchase`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        domain: domain,
        ownerAddress: userInfo.walletAddress,
        ownerEmail: ownerEmail,
        ownerName: ownerName,
        currency: currency,
        demo: demo,
        years: basket?.years,
      }),
    })
      .then((res) => res.json())
      .then(async (data) => {
        await completePaymentIntent(data.paymentId).then(() => {
          if(data.orderId) {
            next()
          } else {
            throw new Error("failed to submit quote")
          }
        })
      })
    }

  useEffect(() => {
    if(basket?.currency == "USD") {
      stripePurchase()
    }
  }, []);

  const stripeOptions = {
    clientSecret,
    appearance,
    fonts: [
      {
        cssSrc: "https://assets.superlink.me/fonts/planar.css",
      },
    ],
    loader: "always" as any,
  };

  return (
    <div style={modalContentStyle(widthStyle)}>
      <div style={topStyle}>
        <button onClick={back} style={backButtonStyle}>
          {backbutton}
        </button>
      </div>
      <div style={checkoutStyle}>
        {basket?.currency == "USD" && clientSecret && (
          <Elements options={stripeOptions} stripe={stripe.stripe}>
            <CheckoutForm clientSecret={clientSecret} userInfo={userInfo} next={next} skipPayment={skipPayment} setHeight={setHeight} />
          </Elements>
        )}
        {basket?.currency == "USD" && !clientSecret && (
          <div style={spinnerStyle}>
            <Spinner width="80px" height="80px" border="10px" />
          </div>
        )}
        {basket?.currency != "USD" && (
          <CryptoCheckoutForm currency={basket?.currency!} cryptoPayment={cryptoPurchase} userInfo={userInfo} next={next} skipPayment={skipPayment} basket={basket} currencyEstimates={currencyEstimates} />
        )}
      </div>
      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingBottom: 25.5,
        }}
      >
        {poweredBy}
      </div>
    </div>
  );
};

const modalContentStyle = (widthStyle: string): React.CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  width: widthStyle,
  height: "100%",
  minHeight: "500px",
});

const backButtonStyle: React.CSSProperties = {
  justifyContent: "center",
  cursor: "pointer",
  alignItems: "center",
  paddingTop: 4,
  width: 32,
  height: 32,
  background: "rgba(0, 0, 0, 0.05)",
  borderRadius: 12,
  border: "none",
  color: "#000000",
};

const topStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  color: "#000000",
  fontSize: "16px",
  fontFamily: "GT Planar Medium",
  gap: 10,
  padding: 24,
  paddingTop: 32,
  paddingBottom: 24,
  borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
};

const checkoutStyle: React.CSSProperties = {
  alignItems: "center",
  alignContent: "center",
  justifyContent: "end",
  color: "#000000",
  fontSize: "16px",
  fontFamily: "GT Planar Medium",
  gap: 10,
  paddingBottom: 14,
  // paddingTop: 14,
  overflow: "visible",
  height: "100%",
  width: "inherit",
};

const spinnerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  height: "85%",
  justifyContent: "center",
};

const stripeStyles = {
  stripeLabel: {
    fontSize: "16px",
    color: "rgba(0, 0, 0, 0.7)",
    padding: "0px 0px 8px 0px",
    fontFamily: "var(--fontFamily)",
  },
  stripeTabLabel: {
    fontSize: "16px",
    color: "rgba(0, 0, 0, 0.7)",
    padding: "8px 0px 8px 0px",
    fontFamily: "var(--fontFamily)",
  },
  inputStripe: {
    backgroundColor: "#ffffff",
    color: "#000000",
    borderRadius: "12px",
    border: "1px solid rgba(0, 0, 0, 0.2)",
    boxShadow: "",
    padding: "16px 24px",
    fontSize: "16px",
  },
  inputStripeFocus: {
    backgroundColor: "#ffffff",
    color: "#000000",
    borderRadius: "12px",
    boxShadow: "",
    border: "1px solid #854EFB",
    padding: "16px 24px",
    fontSize: "16px",
  },
  tabsStripe: {
    backgroundColor: "#ffffff",
    color: "#000000",
    borderRadius: "12px",
    border: "1px solid rgba(0, 0, 0, 0.2)",
    padding: "16px 24px",
    fontSize: "16px",
  },
};

const appearance = {
  variables: {
    colorPrimary: "#0570de",
    colorBackground: "#ffffff",
    colorText: "#30313d",
    colorDanger: "#df1b41",
    fontFamily: "GT Planar Regular",
    spacingUnit: "2px",
    borderRadius: "12px",
    spacingGridRow: "20px",
  },
  rules: {
    ".Tab": stripeStyles.tabsStripe,
    ".Tab--selected": {
      border: "1px solid #854EFB",
      boxShadow: "",
    },
    ".Tab--selected:focus": {
      border: "1px solid #854EFB",
      boxShadow: "",
    },
    ".Tab:focus": {
      border: "1px solid #854EFB",
      boxShadow: "",
    },
    ".Label": stripeStyles.stripeLabel,
    ".DropdownItem": stripeStyles.stripeLabel,
    ".TabLabel": stripeStyles.stripeTabLabel,
    ".Input": stripeStyles.inputStripe,
    ".Input:focus": stripeStyles.inputStripeFocus,
  },
};